import React, { useEffect, useState } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { apiHandler } from '../../api/apihandler';
import { TEACHERS_PEANUT_POLICY_API } from '../../api/constants';
import { formatDisplayDate } from '../../utilities/displayFormat';

// Shared "No Peanut Policy" modal - the real one, previously only reachable
// from the Attendance page's per-student action button. Also rendered from
// notification.js so a teacher clicking a No Peanut Policy notification sees
// this exact modal instead of a separate, limited one. Self-contained:
// fetches its own data from studentId rather than depending on the caller's
// other state.
const PeanutPolicyModal = ({ show, onHide, studentId, studentName }) => {
  const [peanutPolicyData, setPeanutPolicyData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !studentId) {
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setPeanutPolicyData(null);
      try {
        const response = await apiHandler(TEACHERS_PEANUT_POLICY_API, 'POST', {
          student_id: studentId,
        });
        if (cancelled) return;
        if (response.data?.response?.status === 200) {
          setPeanutPolicyData({
            student: response.data.data.student,
            policy: response.data.data.policy,
          });
        }
      } catch (error) {
        console.error('Error loading peanut policy:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, studentId]);

  const formatDate = formatDisplayDate;
  const resolvedStudentName = peanutPolicyData?.student?.name || studentName || 'Student';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered style={{ marginTop: '50px' }}>
      <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', padding: '16px 20px', background: 'white' }}>
        <Modal.Title style={{ fontSize: '15px', fontWeight: '700', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="ri-forbid-line" style={{ fontSize: '18px', color: '#ef4444' }}></i>
          </div>
          Peanut Policy - {resolvedStudentName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto', background: '#f9fafb' }}>
        {peanutPolicyData ? (
          peanutPolicyData.policy ? (
            <div className="container-fluid">
              <Row className="g-3">
                {/* Student Information Card */}
                <Col xs={12}>
                  <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <img
                      src={peanutPolicyData?.student?.photo || 'https://uzone360live.s3.amazonaws.com/noimage.png'}
                      alt={resolvedStudentName}
                      style={{ width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover', border: '2px solid #e5e7eb', flexShrink: 0 }}
                      onError={(e) => {
                        e.target.src = 'https://uzone360live.s3.amazonaws.com/noimage.png';
                      }}
                    />
                    <div>
                      <h6 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                        {resolvedStudentName}
                      </h6>
                      <p style={{ margin: '0', fontSize: '12px', color: '#6b7280' }}>
                        Student ID: {peanutPolicyData?.student?.id || studentId || 'N/A'}
                      </p>
                    </div>
                  </div>
                </Col>
                {/* Policy Information */}
                <Col xs={12}>
                  <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <h6 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="ri-file-list-3-line" style={{ fontSize: '16px', color: '#6366f1' }}></i>
                      Policy Information
                    </h6>

                    <Row className="g-3">
                      <Col md={12}>
                        <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '12px', border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Policy Description</div>
                          <div style={{ fontSize: '13px', color: '#111827', lineHeight: '1.6', fontWeight: '400' }}>
                            {peanutPolicyData.policy.description || 'No description provided'}
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '12px', border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Policy Status</div>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: peanutPolicyData.policy.status === 1 ? '#dcfce7' : '#fee2e2',
                            color: peanutPolicyData.policy.status === 1 ? '#166534' : '#991b1b',
                          }}>
                            {peanutPolicyData.policy.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '12px', border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Created Date</div>
                          <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                            {formatDate(peanutPolicyData.policy.created_at)}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>

                {/* Signature Section */}
                <Col xs={12}>
                  <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <h6 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="ri-quill-pen-line" style={{ fontSize: '16px', color: '#6366f1' }}></i>
                      Parent Signature
                    </h6>

                    <Row className="g-3">
                      <Col md={6}>
                        <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '12px', border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Signature Date</div>
                          <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                            {formatDate(peanutPolicyData.policy.parent_signature_date)}
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Digital Signature</div>
                          {peanutPolicyData.policy.parent_signature ? (
                            <img
                              src={peanutPolicyData.policy.parent_signature}
                              alt="Parent Signature"
                              style={{ maxWidth: '100%', maxHeight: '80px', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: 'white' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div style={{
                            display: peanutPolicyData.policy.parent_signature ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '16px',
                            color: '#6b7280',
                            fontSize: '12px',
                          }}>
                            <i className="ri-image-line" style={{ fontSize: '16px', marginRight: '6px', opacity: 0.5 }}></i>
                            No signature available
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>

                {/* Student Information */}
                <Col xs={12}>
                  <div style={{ background: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <h6 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="ri-user-line" style={{ fontSize: '16px', color: '#6366f1' }}></i>
                      Student Information
                    </h6>

                    <Row className="g-3">
                      <Col md={3}>
                        <div style={{ textAlign: 'center' }}>
                          <img
                            src={peanutPolicyData.student?.photo || 'https://uzone360live.s3.amazonaws.com/noimage.png'}
                            alt={peanutPolicyData.student?.name}
                            style={{ width: '70px', height: '70px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                            onError={(e) => {
                              e.target.src = 'https://uzone360live.s3.amazonaws.com/noimage.png';
                            }}
                          />
                        </div>
                      </Col>

                      <Col md={4}>
                        <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '12px', border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Student Name</div>
                          <div style={{ fontSize: '13px', color: '#111827', fontWeight: '600' }}>
                            {peanutPolicyData.student?.name || 'N/A'}
                          </div>
                        </div>
                      </Col>

                      <Col md={5}>
                        <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '12px', border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Student Email</div>
                          <div style={{ fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                            {peanutPolicyData.student?.email || 'N/A'}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6c757d' }}>
              <i className="ri-forbid-line" style={{ fontSize: '48px', color: '#dc3545', opacity: 0.3, display: 'block', marginBottom: '16px' }}></i>
              <h6 style={{ fontSize: '16px', fontWeight: '600', color: '#495057', marginBottom: '8px' }}>No Peanut Policy Data</h6>
              <p style={{ fontSize: '14px', color: '#6c757d', margin: 0 }}>No peanut policy information is available for this student.</p>
            </div>
          )
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6c757d' }}>
            <div className="spinner-border" style={{ color: '#dc3545' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ fontSize: '14px', marginTop: '16px', margin: 0 }}>Loading peanut policy data...</p>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6c757d' }}>
            <i className="ri-forbid-line" style={{ fontSize: '48px', color: '#dc3545', opacity: 0.3, display: 'block', marginBottom: '16px' }}></i>
            <h6 style={{ fontSize: '16px', fontWeight: '600', color: '#495057', marginBottom: '8px' }}>No Peanut Policy Data</h6>
            <p style={{ fontSize: '14px', color: '#6c757d', margin: 0 }}>No peanut policy information is available for this student.</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PeanutPolicyModal;
